import { LapCounter } from '@cds/scoreboards/v1/shared';
import { EventModel, EventScoreboard, Weather } from '@frontend/sports/betting-offer/feature/model';

import { MyBetsEventModel } from '../../my-bets/models/my-bets-event.model';
import { ScoreboardTimer } from './scoreboard-timer';

export interface IScoreboardLiveAlert {
    liveAlert: boolean;
    sportId: number;
    eventId: string;
    leagueId: number;
    eventGroupId?: number;
    leagueGroupId?: number;
    title: string;
    live: boolean;
}

export interface IEventTimeViewModel {
    startTime: Date;
    isLive: boolean;
    hasStreams: boolean;
    periodInfo: string;
    matchType: number;
    bestOfSets: number;
    bestOfLegs: number;
    timer: ScoreboardTimer | null;
    hasScoreboard: boolean;
    liveAlert: IScoreboardLiveAlert;
    sportId: number;
    periodId: number;
    playersList: number[];
    bestOf: number;
    lapCounter?: LapCounter;
    weather: Weather | null;
}

export abstract class ScoreboardViewModel {
    hasScoreboard: boolean;
    started: boolean;
    hasStreams: boolean;
    periodInfo: string;
    startTime: Date;
    timer: ScoreboardTimer | null;
    liveAlert: IScoreboardLiveAlert;
    sportId: number;
    periodId: number;
    playersList: Array<number>;
    matchType: number;
    bestOfSets: number;
    bestOfLegs: number;
    bestOf: number;
    lapCounter?: LapCounter;
    weather: Weather | null = null;

    protected constructor() {
        this.started = false;
        this.hasStreams = false;
        this.periodInfo = '';
        this.hasScoreboard = false;
        this.timer = null;
        this.sportId = 0;
        this.periodId = 0;
        this.playersList = [];
        this.bestOf = 0;
    }

    getEventTimeViewModel(): IEventTimeViewModel {
        return {
            startTime: this.startTime,
            isLive: this.started,
            hasStreams: this.hasStreams,
            periodInfo: this.periodInfo,
            timer: this.timer,
            hasScoreboard: this.hasScoreboard,
            liveAlert: this.liveAlert,
            sportId: this.sportId,
            periodId: this.periodId,
            playersList: this.playersList,
            matchType: this.matchType,
            bestOfLegs: this.bestOfLegs,
            bestOfSets: this.bestOfSets,
            bestOf: this.bestOf,
            lapCounter: this.lapCounter,
            weather: this.weather,
        };
    }

    protected __fillFromEvent(event: EventModel | MyBetsEventModel): void {
        this.hasStreams = !!event.stream;
        this.startTime = new Date(event.startDate);
        this.sportId = event.sport.id;
        this.periodId = (event.scoreboard.period && event.scoreboard.period.id) || 0;

        this.liveAlert = <IScoreboardLiveAlert>{
            liveAlert: event.liveAlert,
            sportId: event.sport.id,
            eventId: event.id.toString(),
            leagueId: event.league.id,
            eventGroupId: event.groupId,
            leagueGroupId: event.league.parentLeagueId,
            title: event.name,
            live: event.live,
        };
    }

    /**
     * This method is called to fill the view model from event.
     * It calls fillFromScoreboard if scoreboard is available in the event.
     *
     * @param event
     */
    fillFromEvent(event: EventModel | MyBetsEventModel): void {
        this.__fillFromEvent(event);
        this.fillFromScoreboard(event.scoreboard);
    }

    protected __fillFromScoreboard(scoreboard: EventScoreboard): void {
        this.hasScoreboard = true;
        this.started = scoreboard.started;
        this.periodInfo = (scoreboard.period && scoreboard.period.name) || '';
        this.periodId = (scoreboard.period && scoreboard.period.id) || 0;
        this.timer = new ScoreboardTimer(this.startTime, scoreboard);
    }

    /**
     * This method is called to fill viewmodel, when scoreboard object is available
     * It is called only once.
     *
     * @param scoreboard
     */
    protected fillFromScoreboard(scoreboard: EventScoreboard): void {
        this.__fillFromScoreboard(scoreboard);
    }

    /**
     * Called when push update comes for scoreboard.
     *
     * @param scoreboard
     */
    updateFromScoreboard(scoreboard: EventScoreboard): void {
        if (!this.hasScoreboard) {
            this.fillFromScoreboard(scoreboard);
        } else {
            this.__fillFromScoreboard(scoreboard);
        }
    }
}
